import React, { memo } from 'react';
import styled from '@emotion/styled';
import { ITheme, RowRole } from '../../../../../../ui';
import { useUrlForFlightDetails } from '../../../../detail/ui/helpers/flightDetailsUrl';
import { IEnrichedFlight } from '../../../../shared/flight/domain/models/IEnrichedFlight';
import { AirlineColumn } from './Airline';
import { BaggageClaimColumn } from './BaggageClaim';
import { DescriptionColumn } from './Description';
import { DetailsLinkColumn } from './DetailsLink';
import { GateColumn } from './Gate';
import { StatusColumn } from './Status';
import { TimeColumn } from './Time';
import { TypeColumn } from './Type';
import { useTranslation } from '../../../../../shared/i18n/translate/useTranslation';
import themed from '../../../../../../ui/themer/themed';
import Responsive from '../../../../../../ui/utils/Responsive';

interface IProps {
	flight: IEnrichedFlight;
}

export const StyledItem = styled(RowRole)<{}, ITheme>`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	padding: 15px 0;
	border-bottom: ${themed(({ border }) => border.default)};

	&:last-child {
		border-bottom: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: 16px;

		&:last-child {
			border-bottom: ${themed(({ border }) => border.default)};
		}
	}
`;

export const Item = memo(({ flight }: IProps) => {
	const { t } = useTranslation();
	const flightDetailsUrl = useUrlForFlightDetails(flight);

	return (
		<StyledItem data-testid="flight-list-item">
			<TimeColumn
				scheduledTimestamp={flight.scheduledTimestamp}
				bestKnownTimestamp={flight.bestKnownTimestamp}
				isDelayed={flight.isDelayed}
				arrival={flight.arrival}
				role="cell"
			/>

			<DescriptionColumn
				viaAirport={flight.viaAirport}
				codeShareFlightNumber={flight.codeShareFlightNumber}
				airport={flight.viaAirportDetails}
				status={flight.publicStatus}
				arrival={flight.arrival}
				scheduledTimestamp={flight.scheduledTimestamp}
				bestKnownTimestamp={flight.bestKnownTimestamp}
				role="cell"
			/>

			<TypeColumn arrival={flight.arrival} role="cell" />

			<StatusColumn status={flight.publicStatus} role="cell" />

			<AirlineColumn
				airline={flight.airlineDetails}
				icaoOperatingAirline={flight.icaoOperatingAirline}
				role="cell"
			/>

			<GateColumn gate={flight.gate} role="cell" />

			<BaggageClaimColumn baggageBelt={flight.baggageBelt} role="cell" />

			<DetailsLinkColumn
				role="cell"
				href={flightDetailsUrl}
				arrival={flight.arrival}
				screenReaderText={t('go_to_details_for', {
					for: flight.codeShareFlightNumber,
				})}
			/>
		</StyledItem>
	);
});
